import * as React from 'react'
import { useMemo } from 'react'
import { useBalanceSummary } from './hooks'
import { Card, Flex, Typography, Spin, Divider, Tooltip } from 'antd'
import { BankOutlined } from '@ant-design/icons'
import { path } from '../../../../libs'

const { Text } = Typography

const fmt = (v: number) =>
  v.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

export const BalanceSummaryWidget = () => {
  const { balanceSummary, passiveBalances, loading } = useBalanceSummary()

  const { turnover, liabilities, costs, lpBalance, earning, balanceChange } =
    balanceSummary

  const passiveChange = passiveBalances?.balanceChange?.all ?? 0

  const revenue = useMemo(
    () => turnover.value + lpBalance.lpTrade + earning,
    [turnover.value, lpBalance.lpTrade, earning],
  )

  const expenses = useMemo(
    () => liabilities.budgetPayment + costs.commission,
    [liabilities.budgetPayment, costs.commission],
  )

  const profit = revenue - expenses
  const margin = revenue !== 0 ? Math.round((profit / revenue) * 100) : 0
  const totalChange = balanceChange - passiveChange

  return (
    <Card size="small" style={{ flex: 1, minWidth: 340 }}>
      <Spin spinning={loading}>
        <Flex align="center" gap={6} style={{ marginBottom: 12 }}>
          <BankOutlined style={{ color: '#6366f1' }} />
          <Tooltip title="Финансовая сводка за текущий месяц. Доход = Оборот + LP trade + Earning. Расходы = Бюджетные выплаты + Комиссии.">
            <a target="_blank" href={path.finSystem.balanceSummary.entry()} style={{ fontSize: 12, color: 'inherit' }}>
              Balance Summary (current month)
            </a>
          </Tooltip>
        </Flex>

        <Flex gap={16} wrap="wrap">
          <Tooltip title="Оборот + LP trade + Earning">
            <Flex vertical style={{ minWidth: 100, flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Revenue</Text>
              <Text strong style={{ fontSize: 20, fontFamily: 'monospace' }}>
                {fmt(revenue)}
              </Text>
              <Text type="secondary" style={{ fontSize: 10 }}>USD</Text>
            </Flex>
          </Tooltip>

          <Tooltip title="Бюджетные выплаты + Комиссии">
            <Flex vertical style={{ minWidth: 100, flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Expenses</Text>
              <Text strong style={{ fontSize: 20, fontFamily: 'monospace', color: '#ef4444' }}>
                {fmt(expenses)}
              </Text>
              <Text type="secondary" style={{ fontSize: 10 }}>USD</Text>
            </Flex>
          </Tooltip>

          <Tooltip title="Доход - Расходы">
            <Flex vertical style={{ minWidth: 100, flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Profit</Text>
              <Text
                strong
                style={{
                  fontSize: 20,
                  fontFamily: 'monospace',
                  color: profit >= 0 ? '#10b981' : '#ef4444',
                }}
              >
                {fmt(profit)}
              </Text>
              <Text type="secondary" style={{ fontSize: 10 }}>USD</Text>
            </Flex>
          </Tooltip>

          <Tooltip title="Прибыль / Доход x 100%">
            <Flex vertical style={{ minWidth: 60, flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Margin</Text>
              <Text strong style={{ fontSize: 20, fontFamily: 'monospace' }}>
                {margin}
              </Text>
              <Text type="secondary" style={{ fontSize: 10 }}>%</Text>
            </Flex>
          </Tooltip>
        </Flex>

        <Divider style={{ margin: '12px 0' }} />

        <Flex vertical gap={6}>
          <Flex justify="space-between">
            <Text type="secondary" style={{ fontSize: 12 }}>Active balance change</Text>
            <Text
              strong
              style={{
                fontFamily: 'monospace',
                fontSize: 13,
                color: balanceChange >= 0 ? '#10b981' : '#ef4444',
              }}
            >
              {fmt(balanceChange)} USD
            </Text>
          </Flex>
          <Flex justify="space-between">
            <Text type="secondary" style={{ fontSize: 12 }}>Passive balance change</Text>
            <Text strong style={{ fontFamily: 'monospace', fontSize: 13 }}>
              {fmt(passiveChange)} USD
            </Text>
          </Flex>
          <Flex justify="space-between">
            <Text type="secondary" style={{ fontSize: 12 }}>Total change</Text>
            <Text
              strong
              style={{
                fontFamily: 'monospace',
                fontSize: 13,
                color: totalChange >= 0 ? '#10b981' : '#ef4444',
              }}
            >
              {fmt(totalChange)} USD
            </Text>
          </Flex>
        </Flex>

        <Divider style={{ margin: '8px 0' }} />

        <Text type="secondary" style={{ fontSize: 11 }}>
          Profit{' '}
          <Text strong style={{ fontSize: 11 }}>{fmt(profit)} USD</Text>
          {' '}- Passive{' '}
          <Text strong style={{ fontSize: 11 }}>{fmt(passiveChange)} USD</Text>
          {' '}= Change{' '}
          <Text strong style={{ fontSize: 11 }}>{fmt(profit - passiveChange)} USD</Text>
        </Text>
      </Spin>
    </Card>
  )
}
